/**
 * assessmentService.js
 *
 * Standardized clinical assessment instruments with auto-scoring.
 * Stores completed assessments in Firestore `assessments` collection.
 *
 * Supported instruments:
 *   PHQ-9  — Depression (0-27)
 *   GAD-7  — Anxiety (0-21)
 *   C-SSRS — Suicide risk (categorical)
 *   AUDIT  — Alcohol use (0-40)
 *   PCL-5  — PTSD (0-80)
 *   CAGE   — Alcohol screening (0-4)
 */

import {
  collection, doc, setDoc, getDocs,
  query, where, orderBy, onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { logAuditEvent } from "./auditService";

const COLL = "assessments";

function getOrg() {
  try {
    const raw = localStorage.getItem("fieldnotes_user_profile");
    if (raw) return JSON.parse(raw).organization || "default-org";
  } catch (e) {}
  return localStorage.getItem("fieldnotes_user_org") || "default-org";
}

function genId() {
  return `asmt_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

// ─── Instrument Definitions ──────────────────────────────────────

const LIKERT_0_3 = [
  { value: 0, label: "Not at all" },
  { value: 1, label: "Several days" },
  { value: 2, label: "More than half the days" },
  { value: 3, label: "Nearly every day" },
];

export const INSTRUMENTS = {

  "PHQ-9": {
    name: "PHQ-9",
    fullName: "Patient Health Questionnaire-9",
    category: "Depression",
    maxScore: 27,
    options: LIKERT_0_3,
    questions: [
      "Little interest or pleasure in doing things",
      "Feeling down, depressed, or hopeless",
      "Trouble falling or staying asleep, or sleeping too much",
      "Feeling tired or having little energy",
      "Poor appetite or overeating",
      "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
      "Trouble concentrating on things, such as reading the newspaper or watching television",
      "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
      "Thoughts that you would be better off dead, or of hurting yourself in some way",
    ],
    scoreBands: [
      { min: 0, max: 4, label: "Minimal", color: "#68d391", action: "Monitor" },
      { min: 5, max: 9, label: "Mild", color: "#ecc94b", action: "Watchful waiting; repeat at follow-up" },
      { min: 10, max: 14, label: "Moderate", color: "#f6ad55", action: "Treatment plan; consider counseling or pharmacotherapy" },
      { min: 15, max: 19, label: "Moderately Severe", color: "#fc8181", action: "Active treatment with pharmacotherapy and/or psychotherapy" },
      { min: 20, max: 27, label: "Severe", color: "#e53e3e", action: "Immediate initiation of pharmacotherapy; consider referral to mental health specialist" },
    ],
    criticalItem: 8, // "Thoughts of being better off dead" — triggers safety protocol
  },

  "GAD-7": {
    name: "GAD-7",
    fullName: "Generalized Anxiety Disorder-7",
    category: "Anxiety",
    maxScore: 21,
    options: LIKERT_0_3,
    questions: [
      "Feeling nervous, anxious, or on edge",
      "Not being able to stop or control worrying",
      "Worrying too much about different things",
      "Trouble relaxing",
      "Being so restless that it is hard to sit still",
      "Becoming easily annoyed or irritable",
      "Feeling afraid as if something awful might happen",
    ],
    scoreBands: [
      { min: 0, max: 4, label: "Minimal", color: "#68d391", action: "Monitor" },
      { min: 5, max: 9, label: "Mild", color: "#ecc94b", action: "Watchful waiting; repeat at follow-up" },
      { min: 10, max: 14, label: "Moderate", color: "#f6ad55", action: "Consider counseling or pharmacotherapy" },
      { min: 15, max: 21, label: "Severe", color: "#fc8181", action: "Active treatment warranted" },
    ],
    criticalItem: null,
  },

  "C-SSRS": {
    name: "C-SSRS",
    fullName: "Columbia Suicide Severity Rating Scale (Screener)",
    category: "Suicide Risk",
    maxScore: null, // Categorical, not summed
    options: [
      { value: 0, label: "No" },
      { value: 1, label: "Yes" },
    ],
    questions: [
      "Have you wished you were dead or wished you could go to sleep and not wake up?",
      "Have you actually had any thoughts of killing yourself?",
      "Have you been thinking about how you might do this?",
      "Have you had these thoughts and had some intention of acting on them?",
      "Have you started to work out or worked out the details of how to kill yourself? Do you intend to carry out this plan?",
      "Have you ever done anything, started to do anything, or prepared to do anything to end your life?",
    ],
    scoreBands: [
      { min: 0, max: 0, label: "No Risk Identified", color: "#68d391", action: "Continue routine screening" },
      { min: 1, max: 1, label: "Wish to be Dead", color: "#ecc94b", action: "Assess further; safety planning" },
      { min: 2, max: 2, label: "Suicidal Ideation", color: "#f6ad55", action: "Safety plan; increase monitoring" },
      { min: 3, max: 3, label: "Ideation with Method", color: "#fc8181", action: "Immediate safety intervention; crisis protocol" },
      { min: 4, max: 5, label: "Intent or Plan", color: "#e53e3e", action: "IMMEDIATE intervention — crisis team, 988, or ER" },
    ],
    scoring: "highest-yes", // Score = highest question number answered "Yes"
    criticalItem: null, // All items are critical
    isCritical: true,
  },

  "AUDIT": {
    name: "AUDIT",
    fullName: "Alcohol Use Disorders Identification Test",
    category: "Substance Use",
    maxScore: 40,
    questions: [
      "How often do you have a drink containing alcohol?",
      "How many drinks containing alcohol do you have on a typical day when you are drinking?",
      "How often do you have 6 or more drinks on one occasion?",
      "How often during the last year have you found that you were not able to stop drinking once you had started?",
      "How often during the last year have you failed to do what was normally expected of you because of drinking?",
      "How often during the last year have you needed a first drink in the morning to get yourself going after a heavy drinking session?",
      "How often during the last year have you had a feeling of guilt or remorse after drinking?",
      "How often during the last year have you been unable to remember what happened the night before because of your drinking?",
      "Have you or someone else been injured because of your drinking?",
      "Has a relative, friend, doctor, or other health care worker been concerned about your drinking or suggested you cut down?",
    ],
    questionOptions: [
      // Q1
      [{ value: 0, label: "Never" }, { value: 1, label: "Monthly or less" }, { value: 2, label: "2-4 times/month" }, { value: 3, label: "2-3 times/week" }, { value: 4, label: "4+ times/week" }],
      // Q2
      [{ value: 0, label: "1 or 2" }, { value: 1, label: "3 or 4" }, { value: 2, label: "5 or 6" }, { value: 3, label: "7 to 9" }, { value: 4, label: "10 or more" }],
      // Q3-Q8
      ...[3,4,5,6,7,8].map(() => [{ value: 0, label: "Never" }, { value: 1, label: "Less than monthly" }, { value: 2, label: "Monthly" }, { value: 3, label: "Weekly" }, { value: 4, label: "Daily or almost daily" }]),
      // Q9
      [{ value: 0, label: "No" }, { value: 2, label: "Yes, but not in the last year" }, { value: 4, label: "Yes, during the last year" }],
      // Q10
      [{ value: 0, label: "No" }, { value: 2, label: "Yes, but not in the last year" }, { value: 4, label: "Yes, during the last year" }],
    ],
    scoreBands: [
      { min: 0, max: 7, label: "Low Risk", color: "#68d391", action: "Alcohol education" },
      { min: 8, max: 15, label: "Hazardous Use", color: "#ecc94b", action: "Simple advice; brief intervention" },
      { min: 16, max: 19, label: "Harmful Use", color: "#f6ad55", action: "Brief counseling and continued monitoring" },
      { min: 20, max: 40, label: "Possible Dependence", color: "#fc8181", action: "Referral for diagnostic evaluation and treatment" },
    ],
    criticalItem: null,
  },

  "PCL-5": {
    name: "PCL-5",
    fullName: "PTSD Checklist for DSM-5",
    category: "PTSD",
    maxScore: 80,
    options: [
      { value: 0, label: "Not at all" },
      { value: 1, label: "A little bit" },
      { value: 2, label: "Moderately" },
      { value: 3, label: "Quite a bit" },
      { value: 4, label: "Extremely" },
    ],
    questions: [
      "Repeated, disturbing, and unwanted memories of the stressful experience?",
      "Repeated, disturbing dreams of the stressful experience?",
      "Suddenly feeling or acting as if the stressful experience were actually happening again?",
      "Feeling very upset when something reminded you of the stressful experience?",
      "Having strong physical reactions when something reminded you of the stressful experience?",
      "Avoiding memories, thoughts, or feelings related to the stressful experience?",
      "Avoiding external reminders of the stressful experience?",
      "Trouble remembering important parts of the stressful experience?",
      "Having strong negative beliefs about yourself, other people, or the world?",
      "Blaming yourself or someone else for the stressful experience or what happened after it?",
      "Having strong negative feelings such as fear, horror, anger, guilt, or shame?",
      "Loss of interest in activities that you used to enjoy?",
      "Feeling distant or cut off from other people?",
      "Trouble experiencing positive feelings?",
      "Irritable behavior, angry outbursts, or acting aggressively?",
      "Taking too many risks or doing things that could cause you harm?",
      "Being 'superalert' or watchful or on guard?",
      "Feeling jumpy or easily startled?",
      "Having difficulty concentrating?",
      "Trouble falling or staying asleep?",
    ],
    scoreBands: [
      { min: 0, max: 30, label: "Below Threshold", color: "#68d391", action: "Monitor; sub-clinical" },
      { min: 31, max: 32, label: "Threshold", color: "#ecc94b", action: "Provisional PTSD diagnosis; further evaluation" },
      { min: 33, max: 80, label: "Above Threshold", color: "#fc8181", action: "Probable PTSD; treatment indicated" },
    ],
    criticalItem: null,
  },

  "CAGE": {
    name: "CAGE",
    fullName: "CAGE Alcohol Screening Questionnaire",
    category: "Substance Use",
    maxScore: 4,
    options: [
      { value: 0, label: "No" },
      { value: 1, label: "Yes" },
    ],
    questions: [
      "Have you ever felt you should Cut down on your drinking?",
      "Have people Annoyed you by criticizing your drinking?",
      "Have you ever felt bad or Guilty about your drinking?",
      "Have you ever had a drink first thing in the morning to steady your nerves or get rid of a hangover (Eye-opener)?",
    ],
    scoreBands: [
      { min: 0, max: 1, label: "Low Risk", color: "#68d391", action: "No intervention needed" },
      { min: 2, max: 3, label: "Clinically Significant", color: "#f6ad55", action: "Further assessment recommended" },
      { min: 4, max: 4, label: "High Risk", color: "#fc8181", action: "Strong indicator of alcohol problems; evaluate and intervene" },
    ],
    criticalItem: null,
  },
};


// ─── Scoring ─────────────────────────────────────────────────────

/**
 * Score an assessment given answers array.
 * Returns { totalScore, severity, color, action, criticalFlag, subscales? }
 */
export function scoreAssessment(instrumentKey, answers) {
  const inst = INSTRUMENTS[instrumentKey];
  if (!inst) return { totalScore: 0, severity: "Unknown", color: "#718096", action: "" };

  let totalScore;

  if (inst.scoring === "highest-yes") {
    // C-SSRS: score = highest question index answered "Yes" (1)
    totalScore = -1;
    answers.forEach((a, i) => {
      if (a === 1) totalScore = i;
    });
    if (totalScore === -1) totalScore = 0;
    else totalScore = totalScore; // 0-indexed question number
  } else {
    totalScore = answers.reduce((sum, a) => sum + (parseInt(a) || 0), 0);
  }

  // Find severity band
  const band = inst.scoreBands.find(b => totalScore >= b.min && totalScore <= b.max)
    || inst.scoreBands[inst.scoreBands.length - 1];

  // Check critical items
  let criticalFlag = false;
  if (inst.isCritical && totalScore > 0) {
    criticalFlag = true;
  } else if (inst.criticalItem !== null && inst.criticalItem !== undefined) {
    criticalFlag = (answers[inst.criticalItem] || 0) >= 1;
  }

  return {
    totalScore,
    maxScore: inst.maxScore,
    severity: band.label,
    color: band.color,
    action: band.action,
    criticalFlag,
    instrumentName: inst.name,
    category: inst.category,
  };
}


// ─── Firestore CRUD ──────────────────────────────────────────────

/**
 * Save a completed assessment.
 */
export async function saveAssessment(data) {
  const id = genId();
  const org = getOrg();
  const scoring = scoreAssessment(data.instrument, data.answers);

  const record = {
    id,
    organization: org,
    individualId: data.individualId,
    instrument: data.instrument,
    answers: data.answers,
    totalScore: scoring.totalScore,
    maxScore: scoring.maxScore,
    severity: scoring.severity,
    action: scoring.action,
    criticalFlag: scoring.criticalFlag,
    administeredBy: data.administeredBy || "",
    administeredDate: data.administeredDate || new Date().toISOString().split("T")[0],
    notes: data.notes || "",
    createdAt: serverTimestamp(),
  };

  await setDoc(doc(db, COLL, id), record);

  try {
    await logAuditEvent("ASSESSMENT_COMPLETED", {
      individualId: data.individualId,
      instrument: data.instrument,
      score: scoring.totalScore,
      severity: scoring.severity,
      criticalFlag: scoring.criticalFlag,
    });
  } catch (e) {}

  return { ...record, scoring };
}

/**
 * Real-time listener for an individual's assessments.
 */
export function onIndividualAssessments(individualId, callback) {
  const q = query(
    collection(db, COLL),
    where("individualId", "==", individualId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ ...d.data(), id: d.id })));
  }, err => {
    console.error("Assessment listener error:", err);
    callback([]);
  });
}

/**
 * Get trend data: all scores for a specific instrument for an individual.
 */
export function getTrendData(assessments, instrumentKey) {
  return assessments
    .filter(a => a.instrument === instrumentKey)
    .sort((a, b) => {
      const dateA = a.administeredDate || "";
      const dateB = b.administeredDate || "";
      return dateA.localeCompare(dateB);
    })
    .map(a => ({
      date: a.administeredDate,
      score: a.totalScore,
      severity: a.severity,
    }));
}

/**
 * Get available instrument keys.
 */
export function getInstrumentList() {
  return Object.keys(INSTRUMENTS).map(key => ({
    key,
    name: INSTRUMENTS[key].name,
    fullName: INSTRUMENTS[key].fullName,
    category: INSTRUMENTS[key].category,
    questionCount: INSTRUMENTS[key].questions.length,
  }));
}